import { useMemo } from 'react'

import { DIALOG_IDS, modalStore, type TDialogId } from '@core/modal'
import { useActions } from '@core/store'

import { paywallStore } from '../store/paywallStore'
import { type TPaywallRequest } from '../types/paywallTypes'

/**
 * Imperative open/close seam for the paywall — the single entry point for HQ, transcription, upload
 * and embed.
 *
 * Memoised for correctness, not perf: callers put `open` in effect dependencies, and a fresh closure
 * per render would re-open the dialog every render.
 */
export const usePaywallDialog = () => {
    const actions = useActions({
        setRequest: paywallStore.actions.setRequest,
        setCloseReason: paywallStore.actions.setCloseReason,
        openModal: modalStore.actions.open,
        closeModal: modalStore.actions.close,
    })

    return useMemo(
        () => ({
            open: (request: TPaywallRequest) => {
                actions.setCloseReason(null)
                actions.setRequest(request)
                actions.openModal(DIALOG_IDS.paywall)
            },
            closeForAuthRedirect: () => {
                actions.setCloseReason('auth-redirect')
                actions.setRequest(null)
                actions.closeModal()
            },
            close: (returnTo?: TDialogId) => {
                actions.setCloseReason('dismiss')
                actions.setRequest(null)
                // An opener that stepped aside (embed) asks to come back; one active dialog, so it is
                // a swap, not a stack.
                if (returnTo) {
                    actions.openModal(returnTo)

                    return
                }

                actions.closeModal()
            },
        }),
        [actions],
    )
}
